import { uploadService } from '@/services/upload.js'
import { FilesInTransfer } from '@/helpers/filesInTransfer.js'
import { validationCode, MetaDataSource } from '@/enums/'
import store from '@/store'
import {createCancelToken} from "@/helpers/api";

export const allowedFileTypes = ['application/pdf']
export function checkFileType(fileType) {
  if (allowedFileTypes.indexOf(fileType) >= 0) {
    return true
  }
  return false
}
export class FileUploader {
  constructor(letterCategory) {
    this.files = [] // all files
    this.metaDataFiles = [] // files with auto-filed metaData
    this.alertList = {
      UserHasNoPermission: [],
      InvalidMetaTag: [],
      DuplicateFiles: [],
      PageCountValidationFailed: [],
      FileSizeTooLarge: [],
      WhiteLineWithoutSerialSeq: [],
      MetaTagOnSerialLetter: [],
      XMP: [],
      WhiteLineNotParsable: [],
      PdfFileEncrypted:[]
    }
    this.FilesInTransfer = new FilesInTransfer()
    this.letterCategory = letterCategory
    this.chunkSize = parseInt(window.config.VUE_APP_UPLOAD_CHUNK_SIZE) || 3
    this.forceFillMetaData = false
  }
  checkFileSize(file) {
    if (file.size > parseInt(window.config.VUE_APP_UPLOAD_MAX_FILE_SIZE)) {
      file.error = true
      file.canceled = true
      file.cancelUpload()
      this.alertList['FileSizeTooLarge'].push(file)
      this.files.splice(this.files.indexOf(file), 1)
    }
  }
  prepareFiles(files) {
    // reset file-arrays
    this.files = []
    this.metaDataFiles = []
    for (var i = 0; i < files.length; i++) {
      const file = files[i]
      // file-properties
      file.isSerialLetter = this.letterCategory === 'series' ? true : false
      file.timestamp = Date.now()
      file.error = false
      file.canceled = false
      file.uploadCancelToken = createCancelToken()
      file.cancelUpload = function() {
        uploadService.cancelUpload(this.uploadCancelToken)
      }
      // check if filetype match
      if (checkFileType(file.type)) {
        this.files.push(file)
      }
      this.checkFileSize(file)
    }
    return this.files.length
  }
  async upload(fileArray) {
    return new Promise(async (resolve, reject) => {
      this.alertList.DuplicateFiles = fileArray.duplicateFiles
      if (this.prepareFiles(fileArray.uniqueFiles) > 0) {
        await this.uploadFiles().then(() => {
          this.deleteCorruptFiles()
          this.files = this.files.filter(file => !file.error && !file.canceled)
          resolve({
            files: this.files,
            metaDataFiles: this.metaDataFiles,
            needSettings:
              this.files.filter(file => file.needSettings).length > 0,
            alertList: this.alertList
          })
        })
      } else {
        // no unique-file
        reject(this.alertList)
      }
    })
  }
  sliceIntoChunks(arr, chunkSize) {
    const res = []
    for (let i = 0; i < arr.length; i += chunkSize) {
      const chunk = arr.slice(i, i + chunkSize)
      res.push(chunk)
    }
    return res
  }
  async uploadFiles() {
    return new Promise(async resolve => {
      const files = this.files
      const chunks = this.sliceIntoChunks(files, this.chunkSize)
      const promises = []
      let chunkPromises = []
      this.FilesInTransfer.init({
        count: files.length,
        actual: 0,
        inTransfer: true,
        files: files
      })
      // for-loop is used
      // forEach would be async and don't waits vor await / promise
      for (var chunk = 0; chunk < chunks.length; chunk++) {
        chunkPromises = []
        for (var i = 0; i < chunks[chunk].length; i++) {
          if (checkFileType(chunks[chunk][i].type)) {
            const promise = this.uploadFile(chunks[chunk][i])
            chunkPromises.push(promise)
            promises.push(promise)
          }
        }
        await Promise.all(chunkPromises)
      }

      //wait until metadata-tag of all files are checked
      await Promise.all(promises)
      // wait 1s to show the 100% in overlay
      // thats because the animation takes some time
      setTimeout(() => {
        this.FilesInTransfer.update({ actual: files.length, inTransfer: false })
        resolve()
      }, 1000)
    })
  }
  async uploadFile(file) {
    let formData = new FormData()
    formData.append('file', file)
    formData.append('isSerialLetter', file.isSerialLetter)
    return uploadService
      .uploadFile(
        {
          formData: formData,
          fileParams: { fileName: file.name, timestamp: file.timestamp },
          userSettings: store.state.settings
        },
        file.uploadCancelToken || {}
      )
      .then(response => {
        file.id = response.uploadFileId
        file.uploaded = true
        file.metaDataInfo = response.metaDataInfo

        if (this.validatePageCount(response, file)) {
          this.validateMetaData(file, response)
        }
      })
      .catch((e) => {
        console.error("Error uploading file", e)
        file.uploaded = false
        /* istanbul ignore next */
        if (
          file.uploadCancelToken &&
          file.uploadCancelToken.token.reason === undefined
        ) {
          file.error = true // normal error
        } else {
          file.canceled = true // user canceled upload
        }
      })
      .finally(() => {
        this.FilesInTransfer.increaseActual()
      })
  }
  validateMetaData(file, res) {
    const hasSomeMetaDataInfo =
      file.metaDataInfo !== null
        ? Object.values(file.metaDataInfo).some(metaInfo => metaInfo)
        : false
    const hasWhiteLineWithoutSerialSeq =
      hasSomeMetaDataInfo &&
      !file.metaDataInfo.hasSerialSeq &&
      file.metaDataInfo.source === MetaDataSource.WhiteLine &&
      file.isSerialLetter
    const isMetaTagOnSerialLetter =
      hasSomeMetaDataInfo &&
      file.metaDataInfo.source === MetaDataSource.MetaTag &&
      file.isSerialLetter
    const userHasNoPermission =
      res.validationCode === validationCode.UserHasNoPermission &&
      res.printMetaDataTagValue !== 'IAB:6' &&
      res.printMetaDataTagValue !== 'IAB:10'
    const hasInvalidMetaTag =
      res.validationCode === validationCode.InvalidMetaTag
    const hasInvalidXMP = res.validationCode === validationCode.XMP
    const hasInvalidWhiteline = res.validationCode === validationCode.WhiteLineNotParsable
    const hasPasswordProtection = res.validationCode === validationCode.PdfFileEncrypted

    if (userHasNoPermission) {
      this.alertList['UserHasNoPermission'].push(file)
      return
    }
    if (hasInvalidMetaTag) {
      this.alertList['InvalidMetaTag'].push(file)
      return
    }
    if (hasWhiteLineWithoutSerialSeq) {
      this.alertList['WhiteLineWithoutSerialSeq'].push(file)
      return
    }
    if (isMetaTagOnSerialLetter) {
      this.alertList['MetaTagOnSerialLetter'].push(file)
      return
    }
    if (hasInvalidXMP) {
      this.alertList['XMP'].push(file)
      return
    }

    if (hasInvalidWhiteline) {
      this.alertList['WhiteLineNotParsable'].push(file)
      return
    }

    if (hasPasswordProtection) {
      this.alertList['PdfFileEncrypted'].push(file)
      return
    }

    // always set letter- and paperType from response
    // no matter if file need settings
    // user can not change letter- and paperType itself
    // expection: LetterType can change if user switch from duplex to simples (Pages changed)
    file.LetterType = res.letterType
    file.PaperTypes = res.paperTypes

    if (!hasSomeMetaDataInfo && !this.forceFillMetaData) {
      file.needSettings = true
    } else {
      file.needSettings = false
      file.PrintMode = res.printMode
      file.ColorMode = res.colorMode
      file.SplitType = res.splitType
      file.FoldType = res.foldType
      file.CostCenter = res.costCenter
      this.metaDataFiles.push(file) // files with auto-filed metaData
    }
  }
  validatePageCount(response, file) {
    if (response.validationCode === validationCode.PageCountValidationFailed) {
      this.alertList['PageCountValidationFailed'].push(file)
      return false
    }
    return true
  }
  getCorruptFileIds(fileArray) {
    return fileArray.reduce((acc, value) => {
      acc.push(value.id)
      return acc
    }, [])
  }
  deleteCorruptFiles() {
    let fileIds = []
    // delete invalidMetaTagFiles direct from files-array
    // invalidMetaTagFiles don't get any uploadFileId
    this.alertList.InvalidMetaTag.forEach(invalidMetaTagFile => {
      this.files.splice(this.files.indexOf(invalidMetaTagFile), 1)
    })

    // delete pageCountValidationFailedFile direct from files-array
    // PageCountValidationFailed don't get any uploadFileId
    this.alertList.PageCountValidationFailed.forEach(
      pageCountValidationFailedFile => {
        this.files.splice(this.files.indexOf(pageCountValidationFailedFile), 1)
      }
    )

    // delete hasInvalidXMP direct from files-array
    // hasInvalidXMP don't get any uploadFileId
    this.alertList.XMP.forEach(XMPFailedFile => {
      this.files.splice(this.files.indexOf(XMPFailedFile), 1)
    })

    // delete hasInvalidWhiteline direct from files-array
    // hasInvalidWhiteline don't get any uploadFileId
    this.alertList.WhiteLineNotParsable.forEach(WhitelineFailedFile => {
      this.files.splice(this.files.indexOf(WhitelineFailedFile), 1)
    })

    // delete hasPasswordProtection direct from files-array
    // hasPasswordProtection don't get any uploadFileId
    this.alertList.PdfFileEncrypted.forEach(PdfFileEncryptedFile => {
      this.files.splice(this.files.indexOf(PdfFileEncryptedFile), 1)
    })

    fileIds.push(...this.getCorruptFileIds(this.alertList.UserHasNoPermission))
    fileIds.push(
      ...this.getCorruptFileIds(this.alertList.WhiteLineWithoutSerialSeq)
    )
    this.deleteCorruptFilesById(fileIds)
  }
  deleteCorruptFilesById(fileIds) {
    if (fileIds.length > 0) {
      uploadService
        .deleteFile({ uploadFileIds: fileIds })
        .then(deleteFileResponse => {
          deleteFileResponse.deletionSuccess.forEach(deletedFileId => {
            this.files.splice(
              this.files.indexOf(
                this.files.find(file => file.id === deletedFileId)
              ),
              1
            )
          })
        })
    } else {
      return
    }
  }
}
